import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * Timeline — Linea de tiempo con eventos cronologicos animados.
 *
 * params: { events: [{year, label}], accent_color }
 */
export default function Timeline({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { events = [], accent_color = COLORS.accent.blue } = params

  const titleOpacity = fadeIn(frame, 0, 20)
  const lineProgress = interpolate(frame, [10, 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })
  const glowPulse = pulse(frame, 0.05, 0.5, 1.0)

  return (
    <SceneWrapper accent={accent_color} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 48,
          padding: '60px 100px',
        }}
      >
        {/* Titulo */}
        <div
          style={{
            opacity: titleOpacity,
            fontFamily: FONTS.heading,
            fontSize: 28,
            fontWeight: 600,
            color: COLORS.text.muted,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Cronologia
        </div>

        {/* Timeline horizontal */}
        <div
          style={{
            width: '100%',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {/* Linea central con glow */}
          <div
            style={{
              position: 'absolute',
              top: 28,
              left: '5%',
              width: `${lineProgress * 90}%`,
              height: 3,
              background: `linear-gradient(to right, ${accent_color}, ${accent_color}40)`,
              borderRadius: 2,
              boxShadow: GLOW.md(accent_color),
              opacity: 0.7 + glowPulse * 0.3,
            }}
          />
          {/* Traveling glow — bright spot moving along the line */}
          {lineProgress > 0.1 && (
            <div
              style={{
                position: 'absolute',
                top: 23,
                left: `${5 + pulse(frame, 0.03, 0, lineProgress * 90)}%`,
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: accent_color,
                boxShadow: GLOW.lg(accent_color),
                opacity: glowPulse * 0.7,
              }}
            />
          )}

          {/* Eventos */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '0 5%',
              gap: 16,
            }}
          >
            {events.slice(0, 6).map((event, i) => {
              const delay = staggerDelay(i, 8) + 12
              const evOpacity = fadeIn(frame, delay, 15)
              const evScale = entrance(frame, fps, delay, SPRINGS.snappy)
              const isEven = i % 2 === 0

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0,
                    flex: 1,
                    opacity: evOpacity,
                  }}
                >
                  {/* Contenido arriba (pares) */}
                  {isEven && <EventLabel event={event} accent={accent_color} above />}

                  {/* Punto en la linea */}
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: accent_color,
                      border: `3px solid ${COLORS.bg}`,
                      transform: `scale(${evScale})`,
                      zIndex: 2,
                      marginTop: isEven ? 8 : 0,
                      marginBottom: isEven ? 0 : 8,
                      flexShrink: 0,
                      boxShadow: GLOW.sm(accent_color),
                    }}
                  />

                  {/* Contenido abajo (impares) */}
                  {!isEven && <EventLabel event={event} accent={accent_color} above={false} />}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </SceneWrapper>
  )
}

function EventLabel({ event, accent, above }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        paddingBottom: above ? 8 : 0,
        paddingTop: above ? 0 : 8,
        minHeight: 80,
        justifyContent: above ? 'flex-end' : 'flex-start',
      }}
    >
      <div
        style={{
          fontFamily: FONTS.heading,
          fontSize: 26,
          fontWeight: 800,
          color: accent,
          textShadow: `0 0 12px ${accent}40`,
        }}
      >
        {event.year}
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 15,
          color: COLORS.text.secondary,
          textAlign: 'center',
          lineHeight: 1.3,
          maxWidth: 160,
        }}
      >
        {event.label}
      </div>
    </div>
  )
}
